<script setup>
// UIS telephony inbox settings: connection diagnostics, webhook checklist
// (UIS has no API to register notifications — the operator creates them in the
// UIS panel and confirms here), and agent ↔ UIS employee/SIP-line mapping.
//
// Saving a SIP line actually registers it against the UIS gateway with JsSIP
// first, so a wrong password is caught here instead of silently killing calls.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import UisAPI from 'dashboard/api/uis';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioModeToggle from 'dashboard/components-next/call/AudioModeToggle.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const checks = ref([]);
const isChecking = ref(false);
const webhookUrl = ref('');
const webhookSeen = ref({});
const employees = ref([]);
const agents = ref([]);

const WEBHOOK_EVENTS = [
  { key: 'incoming', required: true },
  { key: 'answer', required: true },
  { key: 'lost', required: true },
  { key: 'talk_end', required: true },
  { key: 'call_end', required: true },
  { key: 'recorded', required: false },
  { key: 'outgoing', required: false },
];

const inboxId = computed(() => props.inbox.id);

// Nothing has ever arrived → the notifications are almost certainly not created
// yet in the UIS panel, which is the single most common reason calls never show up.
const noWebhooksYet = computed(
  () => Object.keys(webhookSeen.value).length === 0
);

const mappedCount = computed(
  () => agents.value.filter(a => a.has_sip_password).length
);

const dotClass = status => {
  if (status === 'ok') return 'bg-n-teal-9';
  if (status === 'error') return 'bg-n-ruby-9';
  return 'bg-n-amber-9';
};

const seenLabel = key => {
  const ts = webhookSeen.value[key];
  if (!ts) return t('INBOX_MGMT.UIS.WEBHOOKS.NOT_SEEN');
  const minutes = Math.round((Date.now() / 1000 - ts) / 60);
  return t('INBOX_MGMT.UIS.WEBHOOKS.SEEN', { minutes });
};

const initials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const decorate = agent => ({
  ...agent,
  sip_password: '',
  editing: !agent.has_sip_password,
  verifying: false,
  verifyError: '',
});

const loadAll = async () => {
  try {
    const [statusRes, agentsRes] = await Promise.all([
      UisAPI.webhookStatus(inboxId.value),
      UisAPI.agents(inboxId.value),
    ]);
    webhookUrl.value = statusRes.data.webhook_url;
    webhookSeen.value = statusRes.data.events || {};
    agents.value = (agentsRes.data.agents || []).map(decorate);
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.LOAD_ERROR'));
  }
};

const loadDirectory = async () => {
  try {
    const { data } = await UisAPI.directory(inboxId.value);
    employees.value = data.employees || [];
    autoMatch();
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.DIRECTORY_ERROR'));
  }
};

const autoMatch = () => {
  agents.value.forEach(agent => {
    if (agent.uis_employee_id) return;
    const match = employees.value.find(
      e =>
        e.email &&
        agent.email &&
        e.email.toLowerCase() === agent.email.toLowerCase()
    );
    if (!match) return;
    agent.uis_employee_id = match.id;
    agent.extension = match.extension;
    agent.sip_login = match.sip_login;
  });
};

const runDiagnostics = async () => {
  isChecking.value = true;
  try {
    const { data } = await UisAPI.diagnostics(inboxId.value);
    checks.value = data.checks || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.DIAGNOSTICS_ERROR'));
  } finally {
    isChecking.value = false;
  }
};

const refreshWebhookStatus = async () => {
  try {
    const { data } = await UisAPI.webhookStatus(inboxId.value);
    webhookSeen.value = data.events || {};
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.LOAD_ERROR'));
  }
};

const onEmployeeChange = (agent, employeeId) => {
  const employee = employees.value.find(
    e => String(e.id) === String(employeeId)
  );
  agent.uis_employee_id = employeeId;
  if (employee) {
    agent.extension = employee.extension;
    agent.sip_login = employee.sip_login;
  }
};

// Register the line once against the UIS gateway to prove the password works.
// This is a throwaway registration: it must be dropped immediately, otherwise it
// competes with the agent's own softphone and UIS rings the wrong contact.
const verifySipLine = async (login, password) => {
  const { VivaPhoneEngine } = await import(
    'dashboard/components-next/vivaPhone/engine.js'
  );
  const engine = new VivaPhoneEngine({});
  return new Promise(resolve => {
    let settled = false;
    const finish = (ok, reason) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        engine.unregister();
      } catch (error) {
        // engine already torn down
      }
      // Give the gateway a moment to drop the temporary contact before the
      // agent's own softphone re-registers the same line.
      setTimeout(() => resolve({ ok, reason }), 1200);
    };
    const timer = setTimeout(
      () => finish(false, t('INBOX_MGMT.UIS.AGENTS.VERIFY_TIMEOUT')),
      15000
    );
    engine.addEventListener('registered', () => finish(true));
    engine.addEventListener('registrationfailed', e =>
      finish(false, e.detail?.cause)
    );
    engine.addEventListener('error', e => finish(false, e.detail?.message));
    engine.register({ login, password });
  });
};

const saveAgent = async agent => {
  if (!agent.uis_employee_id) {
    useAlert(t('INBOX_MGMT.UIS.AGENTS.PICK_EMPLOYEE'));
    return;
  }
  agent.verifyError = '';
  agent.verifying = true;
  try {
    if (agent.sip_login && agent.sip_password) {
      const { ok, reason } = await verifySipLine(
        agent.sip_login,
        agent.sip_password
      );
      if (!ok) {
        agent.verifyError =
          reason || t('INBOX_MGMT.UIS.AGENTS.VERIFY_FAILED_GENERIC');
        return;
      }
    }
    await UisAPI.updateAgent(inboxId.value, {
      user_id: agent.user_id,
      uis_employee_id: agent.uis_employee_id,
      extension: agent.extension,
      device_phone: agent.device_phone || '',
      sip_login: agent.sip_login,
      sip_password: agent.sip_password || undefined,
    });
    if (agent.sip_password) agent.has_sip_password = true;
    agent.sip_password = '';
    agent.editing = false;
    useAlert(t('INBOX_MGMT.UIS.AGENTS.SAVED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.AGENTS.SAVE_ERROR'));
  } finally {
    agent.verifying = false;
  }
};

const disconnectAgent = async agent => {
  try {
    await UisAPI.deleteAgent(inboxId.value, agent.user_id);
    agent.uis_employee_id = null;
    agent.extension = null;
    agent.device_phone = '';
    agent.sip_login = '';
    agent.sip_password = '';
    agent.has_sip_password = false;
    agent.editing = true;
    useAlert(t('INBOX_MGMT.UIS.AGENTS.DISCONNECTED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.UIS.AGENTS.SAVE_ERROR'));
  }
};

const employeeName = agent => {
  const employee = employees.value.find(
    e => String(e.id) === String(agent.uis_employee_id)
  );
  return employee ? employee.name : '';
};

const copyWebhookUrl = () => {
  navigator.clipboard?.writeText(webhookUrl.value);
  useAlert(t('INBOX_MGMT.UIS.WEBHOOKS.COPIED'));
};

onMounted(async () => {
  await loadAll();
  await loadDirectory();
});
</script>

<template>
  <div class="flex flex-col gap-6 py-6">
    <!-- Где звук: софтфон в браузере или устройство оператора -->
    <AudioModeToggle :inbox="inbox" />

    <!-- Connection -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.UIS.DIAGNOSTICS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.UIS.DIAGNOSTICS.DESC') }}
          </p>
        </div>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="flex-shrink-0"
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.UIS.DIAGNOSTICS.RUN')"
          @click="runDiagnostics"
        />
      </div>
      <p class="mt-3 text-xs text-n-slate-11 leading-relaxed">
        {{ $t('INBOX_MGMT.UIS.DIAGNOSTICS.IP_NOTE') }}
      </p>
      <ul
        v-if="checks.length"
        class="flex flex-col gap-2 m-0 mt-4 list-none pl-0"
      >
        <li
          v-for="check in checks"
          :key="check.key"
          class="flex items-center gap-3 text-sm"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="dotClass(check.status)"
          />
          <span class="text-n-slate-12">
            {{
              $t(`INBOX_MGMT.ADD.UIS_CHANNEL.CHECKS.${check.key.toUpperCase()}`)
            }}
          </span>
          <span class="ml-auto text-xs text-n-slate-11">
            {{ check.message }}
          </span>
        </li>
      </ul>
    </section>

    <!-- Webhooks -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.UIS.WEBHOOKS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11 max-w-2xl">
            {{ $t('INBOX_MGMT.UIS.WEBHOOKS.DESC') }}
          </p>
        </div>
        <NextButton
          type="button"
          faded
          slate
          sm
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.UIS.WEBHOOKS.REFRESH')"
          @click="refreshWebhookStatus"
        />
      </div>

      <div
        v-if="noWebhooksYet"
        class="flex gap-3 p-3 mt-4 rounded-lg bg-n-amber-3 dark:bg-n-amber-2"
      >
        <span class="size-2 mt-1.5 rounded-full bg-n-amber-9 flex-shrink-0" />
        <p class="text-sm text-n-amber-12 leading-relaxed">
          {{ $t('INBOX_MGMT.UIS.WEBHOOKS.NONE_YET') }}
        </p>
      </div>

      <div class="flex items-center gap-2 mt-4">
        <code
          class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12 font-mono truncate"
        >
          {{ webhookUrl }}
        </code>
        <NextButton
          type="button"
          faded
          blue
          sm
          class="flex-shrink-0"
          :label="$t('INBOX_MGMT.UIS.WEBHOOKS.COPY')"
          @click="copyWebhookUrl"
        />
      </div>

      <ul class="flex flex-col m-0 mt-3 list-none pl-0 divide-y divide-n-weak">
        <li
          v-for="event in WEBHOOK_EVENTS"
          :key="event.key"
          class="flex items-center gap-3 py-2 text-sm"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :class="webhookSeen[event.key] ? 'bg-n-teal-9' : 'bg-n-slate-6'"
          />
          <span class="text-n-slate-12">
            {{
              $t(`INBOX_MGMT.UIS.WEBHOOKS.EVENTS.${event.key.toUpperCase()}`)
            }}
          </span>
          <span
            v-if="event.required"
            class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-ruby-3 text-n-ruby-11"
          >
            {{ $t('INBOX_MGMT.UIS.WEBHOOKS.REQUIRED') }}
          </span>
          <span class="ml-auto text-xs text-n-slate-11 whitespace-nowrap">
            {{ seenLabel(event.key) }}
          </span>
        </li>
      </ul>
    </section>

    <!-- Agent mapping -->
    <section class="p-5 border border-n-weak rounded-xl bg-n-solid-1">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.UIS.AGENTS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11 max-w-2xl">
            {{ $t('INBOX_MGMT.UIS.AGENTS.DESC') }}
          </p>
        </div>
        <span class="flex-shrink-0 text-xs text-n-slate-11 whitespace-nowrap">
          {{ mappedCount }}/{{ agents.length }}
        </span>
      </div>

      <div class="flex flex-col gap-2 mt-4">
        <div
          v-for="agent in agents"
          :key="agent.user_id"
          class="p-4 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-3">
            <span
              class="grid flex-shrink-0 rounded-full size-8 place-items-center bg-n-alpha-2 text-n-slate-11 text-xs font-semibold"
            >
              {{ initials(agent.name) }}
            </span>
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ agent.name }}
              </p>
              <p class="text-xs text-n-slate-11 truncate">{{ agent.email }}</p>
            </div>

            <!-- Configured: status + actions on the same row -->
            <template v-if="!agent.editing">
              <div class="flex items-center gap-2 ml-auto">
                <span
                  class="hidden sm:inline text-xs text-n-slate-11 truncate max-w-32"
                >
                  {{ employeeName(agent) }}
                </span>
                <span
                  class="px-2 py-1 text-xs rounded-md bg-n-teal-3 text-n-teal-11 font-mono whitespace-nowrap"
                >
                  {{ agent.sip_login }}
                </span>
                <NextButton
                  type="button"
                  faded
                  slate
                  sm
                  :label="$t('INBOX_MGMT.UIS.AGENTS.EDIT')"
                  @click="agent.editing = true"
                />
                <NextButton
                  type="button"
                  faded
                  ruby
                  sm
                  :label="$t('INBOX_MGMT.UIS.AGENTS.DISCONNECT')"
                  @click="disconnectAgent(agent)"
                />
              </div>
            </template>
          </div>

          <!-- Editing: aligned grid so rows don't stagger -->
          <div v-if="agent.editing" class="mt-4">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.EMPLOYEE') }}
                <select
                  :value="agent.uis_employee_id"
                  class="mt-1 mb-0"
                  @change="onEmployeeChange(agent, $event.target.value)"
                >
                  <option value="">—</option>
                  <option v-for="e in employees" :key="e.id" :value="e.id">
                    {{ e.name }}{{ e.extension ? ` (${e.extension})` : '' }}
                  </option>
                </select>
              </label>
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.DEVICE_PHONE') }}
                <input
                  v-model="agent.device_phone"
                  type="text"
                  class="mt-1 mb-0"
                  :placeholder="
                    $t('INBOX_MGMT.UIS.AGENTS.DEVICE_PHONE_PLACEHOLDER')
                  "
                />
                <span
                  class="mt-1 block text-[11px] leading-snug text-n-slate-10"
                >
                  {{ $t('INBOX_MGMT.UIS.AGENTS.DEVICE_PHONE_HINT') }}
                </span>
              </label>
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.SIP_LOGIN') }}
                <input
                  v-model="agent.sip_login"
                  type="text"
                  class="mt-1 mb-0"
                  placeholder="0561408"
                />
              </label>
              <label class="mb-0 text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.SIP_PASSWORD') }}
                <input
                  v-model="agent.sip_password"
                  type="password"
                  class="mt-1 mb-0"
                  :placeholder="
                    agent.has_sip_password
                      ? $t('INBOX_MGMT.UIS.AGENTS.PASSWORD_SET')
                      : ''
                  "
                />
              </label>
            </div>
            <div class="flex flex-wrap items-center gap-3 mt-3">
              <NextButton
                type="button"
                solid
                blue
                sm
                :is-loading="agent.verifying"
                :label="$t('INBOX_MGMT.UIS.AGENTS.VERIFY_AND_SAVE')"
                @click="saveAgent(agent)"
              />
              <span v-if="agent.verifying" class="text-xs text-n-slate-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.VERIFYING') }}
              </span>
              <span v-if="agent.verifyError" class="text-xs text-n-ruby-11">
                {{ $t('INBOX_MGMT.UIS.AGENTS.VERIFY_FAILED') }}:
                {{ agent.verifyError }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
